import { useEffect, useRef } from 'react';
import { BackgroundAccessibilityLockService } from '../../../services/background-accessibility-lock.service';
import { BodyScrollLockService } from '../../../services/body-scroll-lock.service';

function FocusTrap({ isVisible, focusOnFirstElement = false, children, onEscape }: { isVisible: boolean; focusOnFirstElement?: boolean; children: React.ReactNode; onEscape?: () => void }) {
  const ref = useRef<HTMLDivElement>(null!);
  const onEscapeRef = useRef(onEscape);
  onEscapeRef.current = onEscape;

  useEffect(() => {
    const refElement = ref.current;
    if (!refElement) return;

    const focusableElements = refElement.querySelectorAll<HTMLElement>('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    const firstChild = refElement.firstElementChild as HTMLElement | null;

    function handleTabKeyPress(event: KeyboardEvent) {
      if (event.key === 'Tab' && firstElement && lastElement) {
        const activeElement = document.activeElement;
        const isFocusInside = refElement.contains(activeElement);

        if (!isFocusInside) {
          event.preventDefault();
          if (event.shiftKey) {
            lastElement.focus();
          } else {
            firstElement.focus();
          }
        } else if (event.shiftKey && activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
        } else if (!event.shiftKey && activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      }
    }

    function handleEscapeKeyPress(event: KeyboardEvent) {
      if (event.key === 'Escape' && onEscapeRef.current) {
        onEscapeRef.current();
      }
    }

    function handleFocusIn(event: FocusEvent) {
      const target = event.target as HTMLElement;

      if (target && !refElement.contains(target)) {
        if (firstElement) {
          firstElement.focus();
        } else {
          if (firstChild) {
            firstChild.focus();
          } else {
            refElement.focus();
          }
        }
      }
    }

    if (isVisible) {
      const releaseBackgroundAccessibilityLock = BackgroundAccessibilityLockService.acquire(refElement);
      const releaseBodyScrollLock = BodyScrollLockService.acquire();

      if (focusOnFirstElement && firstElement) {
        firstElement.focus();
      } else {
        if (firstChild) {
          firstChild.tabIndex = -1;
          firstChild.focus();
        } else {
          refElement.focus();
        }
      }

      document.addEventListener('keydown', handleTabKeyPress);
      document.addEventListener('keydown', handleEscapeKeyPress);
      document.addEventListener('focusin', handleFocusIn);

      return () => {
        releaseBackgroundAccessibilityLock();
        releaseBodyScrollLock();

        document.removeEventListener('keydown', handleTabKeyPress);
        document.removeEventListener('keydown', handleEscapeKeyPress);
        document.removeEventListener('focusin', handleFocusIn);
      };
    }
  }, [isVisible, focusOnFirstElement]);

  return (
    <>
      {isVisible && (
        <div tabIndex={-1} ref={ref}>
          {children}
        </div>
      )}
    </>
  );
}

export default FocusTrap;
